import { api } from "./client";

export interface MpDungeonMember {
  user_id: number;
  name: string;
  hero_class: string;
  level: number;
  br: number;
  ready: boolean;
  slot: number;
}

export interface MpDungeonRoomDetail {
  room_id: string;
  host_user_id: number;
  dungeon_level: number;
  difficulty: string;
  status: string;
  seconds_remaining: number;
  members: (MpDungeonMember | null)[];
  is_host: boolean;
  is_leader: boolean;
  all_ready: boolean;
  can_start: boolean;
}

export interface MpDungeonRoomSummary {
  room_id: string;
  host_user_id: number;
  host_name: string;
  host_level: number;
  host_br: number;
  dungeon_level: number;
  difficulty: string;
  member_count: number;
  seconds_remaining: number;
}

export interface MpRunNode {
  id: string;
  row: number;
  col: number;
  display_type: string;
  cleared: boolean;
  reachable: boolean;
}

export interface MpBattleRewards {
  gold: number;
  xp: number;
  shards: number;
  shard_level: number;
  legendary_stones: number;
  dungeon_cleared?: boolean;
}

export interface MpPendingBattle {
  node_id: string;
  log: import("../types").BattleLogEvent[];
  won: boolean;
  acked: boolean;
  rewards?: MpBattleRewards | null;
}

export interface MpDungeonRunState {
  room_id: string;
  dungeon_level: number;
  difficulty: string;
  status: string;
  host_user_id: number;
  leader_user_id: number;
  is_leader: boolean;
  members: MpDungeonMember[];
  nodes: MpRunNode[];
  edges: { from: string; to: string }[];
  current_node_id: string | null;
  pending_battle: MpPendingBattle | null;
}

export interface MpDungeonMeResponse {
  status: string;
  room?: MpDungeonRoomDetail | null;
  run?: MpDungeonRunState | null;
  expired_message?: string | null;
}

export interface MpDungeonConfig {
  enabled: boolean;
  levels: number[];
  difficulties: string[];
  room_ttl_sec: number;
  energy_cost: number;
  start_energy_cost: number;
  daily_limit_enabled: boolean;
  hero_level: number;
  used_today: boolean;
  bonus_runs: number;
  can_enter_today: boolean;
}

export const mpDungeonsApi = {
  getConfig: () => api.get<MpDungeonConfig>("/mp-dungeons/config"),

  createRoom: (dungeon_level: number, difficulty: string) =>
    api.post<{ room: MpDungeonRoomDetail }>("/mp-dungeons/rooms", { dungeon_level, difficulty }),

  listRooms: () => api.get<{ rooms: MpDungeonRoomSummary[] }>("/mp-dungeons/rooms"),

  getMe: () => api.get<MpDungeonMeResponse>("/mp-dungeons/me"),

  joinRoom: (room_id: string) => api.post<MpDungeonMeResponse>(`/mp-dungeons/rooms/${room_id}/join`),

  leaveRoom: (room_id: string) => api.delete<MpDungeonMeResponse>(`/mp-dungeons/rooms/${room_id}`),

  setReady: (room_id: string, ready: boolean) =>
    api.post<MpDungeonMeResponse>(`/mp-dungeons/rooms/${room_id}/ready`, { ready }),

  startDungeon: (room_id: string) => api.post<MpDungeonMeResponse>(`/mp-dungeons/rooms/${room_id}/start`),

  kickMember: (room_id: string, target_user_id: number) =>
    api.post<MpDungeonMeResponse>(`/mp-dungeons/rooms/${room_id}/kick/${target_user_id}`),

  move: (node_id: string) => api.post<{ run: MpDungeonRunState; info_messages: string[] }>("/mp-dungeons/move", { node_id }),

  ackBattle: () => api.post<{ run: MpDungeonRunState; info_messages: string[] }>("/mp-dungeons/ack-battle"),

  abandon: () => api.post<MpDungeonMeResponse>("/mp-dungeons/abandon"),
};
